import type { CommunicationGraphDraftAttachment } from '../../../../../src/contracts/communicationGraphDraftContracts';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const DIRECT_ATTACHMENT_LIMIT = 3 * 1024 * 1024;
const UPLOAD_CHUNK_SIZE = 10 * 320 * 1024;

const graphError = async (response: Response): Promise<Error> => {
  const payload = await response.json().catch(() => undefined) as { error?: { message?: string } } | undefined;
  return new Error(payload?.error?.message ?? `Microsoft Graph returned HTTP ${response.status}.`);
};

const graphFetch = async (token: string, url: string, init: RequestInit): Promise<Response> => {
  const response = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers ?? {}) }
  });
  if (!response.ok) throw await graphError(response);
  return response;
};

const bytesFromBase64 = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
};

const addAttachment = async (token: string, messageId: string, attachment: CommunicationGraphDraftAttachment): Promise<void> => {
  if (attachment.size <= DIRECT_ATTACHMENT_LIMIT) {
    await graphFetch(token, `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}/attachments`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: attachment.name,
        contentType: attachment.contentType,
        contentBytes: attachment.base64
      })
    });
    return;
  }

  const sessionResponse = await graphFetch(token, `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}/attachments/createUploadSession`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ AttachmentItem: { attachmentType: 'file', name: attachment.name, size: attachment.size } })
  });
  const session = await sessionResponse.json() as { uploadUrl?: string };
  if (!session.uploadUrl) throw new Error('Microsoft Graph did not provide an attachment upload URL.');
  const uploadUrl = new URL(session.uploadUrl);
  if (!['graph.microsoft.com', 'outlook.office.com', 'outlook.office365.com'].includes(uploadUrl.hostname.toLowerCase())) {
    throw new Error('Microsoft Graph returned an unexpected attachment upload host.');
  }
  const bytes = bytesFromBase64(attachment.base64);
  for (let start = 0; start < bytes.length; start += UPLOAD_CHUNK_SIZE) {
    const endExclusive = Math.min(bytes.length, start + UPLOAD_CHUNK_SIZE);
    const chunk = bytes.slice(start, endExclusive);
    const response = await fetch(uploadUrl.toString(), {
      method: 'PUT',
      headers: {
        'content-length': String(chunk.byteLength),
        'content-range': `bytes ${start}-${endExclusive - 1}/${bytes.length}`
      },
      body: chunk as unknown as BodyInit
    });
    if (!response.ok) throw await graphError(response);
  }
};

export const createOutlookGraphDraft = async (input: {
  token: string;
  subject: string;
  plainText: string;
  html?: string;
  recipients: string[];
  attachments: CommunicationGraphDraftAttachment[];
}): Promise<{ id: string; webLink: string }> => {
  const response = await graphFetch(input.token, `${GRAPH_BASE}/me/messages`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      subject: input.subject,
      body: { contentType: input.html ? 'HTML' : 'Text', content: input.html ?? input.plainText },
      toRecipients: input.recipients.map(address => ({ emailAddress: { address } }))
    })
  });
  const message = await response.json() as { id?: string; webLink?: string };
  if (!message.id || !message.webLink) throw new Error('Microsoft Graph created a draft without returning an Outlook link.');
  try {
    for (const attachment of input.attachments) await addAttachment(input.token, message.id, attachment);
  } catch (error) {
    const cleanup = await fetch(`${GRAPH_BASE}/me/messages/${encodeURIComponent(message.id)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${input.token}` }
    }).catch(() => undefined);
    const detail = (error as Error).message;
    throw new Error(cleanup?.ok
      ? detail
      : `${detail} Microsoft could not confirm removal of the incomplete draft, so check your Drafts folder before retrying.`);
  }
  return { id: message.id, webLink: message.webLink };
};
